// ── PositionSidebar — Floating position tracker popup ──────────────
// Renders as a fixed-position panel anchored to the right edge so it can
// overlay the table + detail panel without fighting either for layout
// space. The crosshair button in the toolbar opens/closes it; click
// outside or Escape dismisses.
// Reads from accountStore + marketStore via shared usePositionPnl hook.

import { useState, useEffect, useRef, useMemo, useId } from 'react';
import { unsupportedVenue } from '../../lib/venueGuard';
import { ChevronDown, Crosshair, X, ExternalLink, Receipt, FoldVertical, UnfoldVertical, Info } from 'lucide-react';
import { cn } from '@/lib/infra/utils';

const SIDEBAR_WIDTH_KEY = 'positionSidebar.width';
const SIDEBAR_WIDTH_DEFAULT = 384; // w-96
const SIDEBAR_WIDTH_MIN = 256;
const SIDEBAR_WIDTH_MAX = 800;
import { useAccountStore } from '../../stores/accountStore';
import { isKalshiCancelPending } from '../../lib/trading/kalshiCancelLifecycle';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useAuthStore } from '../../stores/authStore';
import { editOrderKeepingPin } from '../../stores/trackerPins';
import { describeWithheldTotal, withheldHeadlineText } from '../../lib/accounting/unrealizedCoverage';
import { usePositionPnl, MTM_PNL_TOOLTIP } from '../../hooks/ui/usePositionPnl';
import { usePolyBalance } from '../../hooks/usePolyBalance';
import { usePolyUnrealizedPnl } from '../../hooks/usePolyUnrealizedPnl';
import { usePolymarketRelayOrders } from '../../hooks/usePolymarketRelayOrders';
import { describePolyMarketCached } from '../../lib/polymarket/describeOrder';
import { explainPolyError } from '../../lib/polymarket/errors';
import { polyOpenOrderRemaining } from '../../lib/polymarket/relay';
import { describePolyCancelRow } from '../../lib/trading/polyCancelPresentation';
import { usePolyCancelStates } from '../../hooks/usePolyCancelStates';
import { ActivityTicker } from '../../components/ActivityTicker';
import { KalshiCancelSyncNotice } from '../../components/KalshiCancelSyncNotice';
import { getAudioContext } from '../../lib/audio/audioContext';
import { describeMarket } from '../../lib/kalshiTicker';
import { KALSHI_PHASE_TEXT } from '../../lib/kalshiAccountPhase';
import { useKalshiAccountPhase } from '../../hooks/useKalshiAccountPhase';
import { ConfirmModal } from '../../components/atoms/Modal';
import { VenueChip } from '../../components/atoms/VenueChip';
import { MarketTypeLeadText } from '../../components/cards/cardKit';
import { ticketLabel } from '../../components/cards/cardFormat';
import { EditOrderPriceModal, OrderActionButtons } from '../../components/molecules';
import { HelpPopover } from '../../components/molecules/HelpPopover';
import { fmtQty } from '../../components/cards/cardFormat';
import { kalshiReportedBalanceTitle } from '../../lib/kalshiBalance';
import { ORG_BALANCE_HIDDEN_LABEL, ORG_BALANCE_HIDDEN_TITLE } from '../../lib/orgBalanceHidden';
import { usOrderNeedsFillVisibility } from '../../lib/polymarketUs/fillProgress';
import { presentUsOrder, presentUsPosition, UNAVAILABLE } from '../../lib/polymarketUs/portfolioModel';
import { usPositionEconomics } from '../../lib/polymarketUs/positionEconomics';
import { useUsPositionMarks } from '../../hooks/useUsPositionMarks';
import { useUsAccountPresence } from '../../hooks/useUsAccountPresence';
import { useSharedNow } from '../../lib/infra/sharedClock';
import { usMarketLabel } from '../../lib/polymarketUs/marketLabel';
import { usReadStatus } from '../../lib/polymarketUs/readStatus';
import { useUsRail, type UsRail } from './useUsRail';
import { usRailPrice, usRailReadTime, type UsRailAccount } from './usRailModel';
import { usCancelConfirmMessage, usRailCancelControl, useUsRailCancel } from './useUsRailCancel';
import { UsBalanceSlot } from './UsBalanceSlot';
import type { UsOrderRow } from '../../lib/polymarketUs/types';

// Local edit-modal target — same shape as PositionTracker's EditOrderData
// but Kalshi-only here (poly rows are display + cancel; poly price edits
// live on the Positions page).
interface SidebarEditOrder {
  orderId: string;
  marketLabel: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  remainingCount: number;
  currentPriceCents: number;
  ticker: string;
}

function formatDollars(cents: number): string {
  const abs = Math.abs(cents);
  const sign = cents >= 0 ? '+' : '-';
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

function formatCents(cents: number): string {
  return `${cents}\u00A2`;
}

const NO_US_CATALOGUE = { getMarket: () => null };
const NO_US_ACCOUNTS: readonly UsRailAccount[] = [];

/** Same owned inventory and shared market feed as the rail. No account read,
 * catalogue request or private timer; missing coverage is never a zero total. */
function UsRailUnrealized({ rail, showPnl, insideButton = false, descriptionId }: {
  rail: Pick<UsRail, 'hasUs' | 'status' | 'read'>; showPnl: boolean; insideButton?: boolean; descriptionId?: string;
}) {
  const sourceId = useId();
  const accounts = rail.hasUs === true ? rail.read?.accounts ?? NO_US_ACCOUNTS : NO_US_ACCOUNTS;
  const rows = useMemo(() => accounts.flatMap(a => a.positions), [accounts]);
  const slugs = useMemo(() => rows.map(row => row.target.marketSlug), [rows]);
  const marks = useUsPositionMarks(`position-sidebar-us-${sourceId}`, slugs, NO_US_CATALOGUE, 0);
  const now = useSharedNow(1000, rows.length > 0);
  const accountsComplete = rail.hasUs === true && rail.status === 'ready' && rail.read !== null
    && accounts.every(a => a.error === null && a.positionsError === null && a.positionsComplete === true
      && a.scope !== null && a.carriedFromMs === undefined)
    && new Set(accounts.map(a => a.accountId)).size === accounts.length;
  const seen = new Set<string>();
  let total = 0, priced = 0, unique = true;
  for (const row of rows) {
    const identity = JSON.stringify([row.scope.scopeKey, row.target.marketSlug, row.target.outcome]);
    if (seen.has(identity)) { unique = false; continue; }
    seen.add(identity);
    const economics = usPositionEconomics(row, marks.getMarket(row.target.marketSlug), now);
    if (economics.pnlDollars !== null) { total += economics.pnlDollars * 100; priced++; }
  }
  const value = accountsComplete && unique && priced === rows.length
    && Number.isFinite(total) && Math.abs(total) <= Number.MAX_SAFE_INTEGER ? total : null;
  const help = !showPnl ? 'P&L hidden — toggle on the Positions page'
    : !accountsComplete ? 'Polymarket US unrealized P&L unavailable: current holdings have not been read for every account.'
      : !unique ? 'Polymarket US unrealized P&L unavailable: duplicate holding identities.'
        : value === null ? `Polymarket US unrealized P&L withheld: ${priced}/${rows.length} holdings have complete entry cost, entry fees and a current exact-outcome quote.`
          : `${rows.length}/${rows.length} Polymarket US holdings priced from current exact-outcome midpoint, recorded entry cost and fees, and estimated closing fees.`;
  if (rail.hasUs === false) return null;
  return <span className="inline-flex items-baseline gap-1.5" data-testid="rail-us-unrealized" title={help}
    aria-label={`Polymarket US unrealized P&L. ${help}`}>
    <span className="text-[10px] text-gray-500">US</span>
    <span className={cn('font-mono text-sm font-semibold', !showPnl || value === null ? 'text-muted-foreground'
      : value >= 0 ? 'text-positive' : 'text-negative')} data-testid="rail-us-unrealized-value">
      {!showPnl ? '•••' : value === null ? '—' : formatDollars(value)}
    </span>
    {descriptionId && <span id={descriptionId} className="sr-only">Polymarket US unrealized P&amp;L: {
      !showPnl ? 'hidden' : value === null ? 'unknown' : formatDollars(value)}. {help}</span>}
    {!insideButton && <HelpPopover label="Polymarket US unrealized P&L details"
      trigger={<Info size={10} aria-hidden="true" />}
      triggerClassName="inline-flex shrink-0 items-center rounded p-0.5 text-muted-foreground hover:text-foreground"
      srDescription={help} panelWidth={280}>{help}</HelpPopover>}
  </span>;
}

/** A dollar figure grouped at two decimals, as the rail's balances read ("$9,332,667.11"). */
function groupedDollars(dollars: number): string {
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type PositionsSyncView = { synced: boolean; consecutiveFailures: number; lastError: string | null };

/** Headline tooltip (#881 review F4): says WHY a value is withheld. An
 *  unreadable snapshot is named as such; a failed refresh after a good
 *  snapshot withholds the account total on that known failed read (counted,
 *  never aged, so no freshness deadline is implied) while rows keep the last
 *  readable holdings. */
function kalshiPnlTitle(
  totalPnl: number | null,
  coverage: { pricedPositions: number; totalPositions: number; pricedValueCents: number },
  positionsSync: PositionsSyncView,
  unpricedSummary: string | null,
): string {
  if (!positionsSync.synced) {
    return positionsSync.lastError
      ? `Kalshi positions snapshot unreadable (${positionsSync.lastError}); P&L withheld`
      : 'Kalshi positions snapshot not received yet; P&L withheld';
  }
  const n = positionsSync.consecutiveFailures;
  if (n > 0) {
    return `Kalshi account P&L withheld: the last ${n} position refresh${n === 1 ? '' : 'es'} failed`
      + `${positionsSync.lastError ? ` (${positionsSync.lastError})` : ''}; rows show the last readable snapshot`;
  }
  return totalPnl === null
    ? `Kalshi unrealized P&L unavailable: ${describeWithheldTotal(coverage, unpricedSummary, formatDollars)}`
    : 'Kalshi account-wide unrealized P&L';
}

/** Short visible warning beside the headline. Carries no money and no
 *  position counts, so it stays visible with P&L hidden; the reason rides
 *  the tooltip only when P&L is shown. */
function positionsRefreshWarning(positionsSync: PositionsSyncView): string | null {
  if (!positionsSync.synced) return positionsSync.lastError ? 'Positions unreadable' : null;
  return positionsSync.consecutiveFailures > 0
    ? `Positions refresh failed ×${positionsSync.consecutiveFailures}`
    : null;
}

export function PositionsRefreshWarning({ positionsSync, showPnl, title }: { positionsSync: PositionsSyncView; showPnl: boolean; title: string }) {
  const text = positionsRefreshWarning(positionsSync);
  if (!text) return null;
  return (
    <span
      className="block text-[10px] leading-4 font-medium text-amber-500"
      data-testid="positions-refresh-warning"
      title={showPnl ? title : 'Kalshi positions refresh problem; P&L hidden'}
    >
      {text}
    </span>
  );
}

/** A Polymarket US status line in the rail: the words a trader acts on, and a
 * one-sentence quick reference behind them (never the only place state lives). */
type UsRailLine = { text: string; help: string };

/**
 * Where one account stands for the rail's US orders or holdings: read now
 * ('current'), shown from an earlier read ('kept', with that read's time) or
 * never read ('unknown': nothing of it is shown). `whole`: the latest refresh
 * failed outright, so every account the kept read had is from that read.
 */
type UsRailAccountStanding =
  | { account: UsRailAccount; state: 'current' }
  | { account: UsRailAccount; state: 'kept'; atMs: number | null }
  | { account: UsRailAccount; state: 'unknown'; code: string };

function usRailStanding(kind: 'orders' | 'positions', account: UsRailAccount, whole: boolean, readAtMs: number | null): UsRailAccountStanding {
  // Holdings that were not read even in the read being kept are still unknown.
  if (kind === 'positions' && account.positionsError !== null) return { account, state: 'unknown', code: account.positionsError };
  if (account.error !== null && account.carriedFromMs === undefined) return { account, state: 'unknown', code: account.error };
  if (account.carriedFromMs !== undefined) return { account, state: 'kept', atMs: account.carriedFromMs };
  return whole ? { account, state: 'kept', atMs: readAtMs } : { account, state: 'current' };
}

const usAccountName = (a: UsRailAccount) => `account …${a.accountId.slice(-4)}`;
const accountCount = (n: number) => `${n} account${n === 1 ? '' : 's'}`;

/**
 * The amber US line under the rail's orders or holdings. A failed check is
 * never "no US": while it is not known whether the login has US (the shared
 * account check failed too), a failed read still says it could not check. A
 * failed refresh keeps the last rows, dimmed, and says when they were read; so
 * does a single account whose read failed (its rows are carried). The time
 * shown is the oldest of the kept reads, so kept rows never look fresher than
 * they are. An account never read is never folded into "from last read": it is
 * counted as not checked, and its quick reference says so, account by account.
 */
function usRailStatusLine(
  kind: 'orders' | 'positions',
  rail: Pick<UsRail, 'hasUs' | 'status' | 'error' | 'read'> & { readAtMs?: number | null },
): UsRailLine | null {
  if (rail.hasUs === false) return null;
  const unknownWarn = kind === 'orders' ? 'US orders may still be resting' : 'US holdings may not all be shown';
  const fullList = 'Retrying every 10 s; Positions → Polymarket US shows the full list.';
  const whole = rail.status === 'unavailable';
  const code = rail.error ?? 'US_READ_UNAVAILABLE';
  const line = (state: 'unavailable' | 'stale' | 'incomplete', help: string, context?: string, reasons: string[] = []) => {
    const status = usReadStatus(kind, state, { detail: help, context, reasons });
    return { text: status.text, help: status.title };
  };
  if (whole && !rail.read) {
    return line('unavailable', `${unknownWarn}. ${fullList}`, undefined, [code]);
  }
  if (!whole && rail.hasUs !== true) return null;
  const accounts = rail.read?.accounts ?? [];
  const standings = accounts.map((a) => usRailStanding(kind, a, whole, rail.readAtMs ?? null));
  const kept = standings.filter((s): s is Extract<UsRailAccountStanding, { state: 'kept' }> => s.state === 'kept');
  const unknown = standings.filter((s): s is Extract<UsRailAccountStanding, { state: 'unknown' }> => s.state === 'unknown');
  if (kept.length > 0 || unknown.length > 0) {
    const times = kept.flatMap((s) => (s.atMs === null ? [] : [s.atMs]));
    // The oldest kept read; without a time, no time is claimed.
    const at = times.length === kept.length && times.length > 0 ? `last read ${usRailReadTime(Math.min(...times))}` : 'the last read';
    const unknownCodes = unknown.map((s) => `${usAccountName(s.account)}: ${s.code}`).join(', ');
    if (kept.length === 0) {
      return line('incomplete',
        `${whole ? `The latest Polymarket US refresh failed (${code}). ` : ''}The Polymarket US check failed (${unknownCodes}). ${unknownWarn}. ${fullList}`,
        `${accountCount(unknown.length)} unreadable`, unknown.map(s => s.code));
    }
    const keptList = kept.map((s) => `${usAccountName(s.account)}: ${s.account.error !== null ? `${s.account.error}, ` : ''}read ${
      s.atMs === null ? 'earlier' : usRailReadTime(s.atMs)}`).join('; ');
    const help = [
      whole ? `The latest Polymarket US refresh failed (${code}).` : null,
      `Showing the last successful read (${keptList}); it may not reflect fills or cancels since.`,
      unknown.length > 0 ? `Not read (${unknownCodes}); ${unknownWarn}.` : null,
      unknown.length > 0 ? fullList : 'Retrying every 10 s.',
    ].filter((s): s is string => s !== null).join(' ');
    if (unknown.length > 0) {
      return line('incomplete', help, `${accountCount(unknown.length)} unreadable · ${accountCount(kept.length)} from ${at}`, unknown.map(s => s.code));
    }
    // The kept read found none of this kind: say that, not "rows".
    const keptRows = kept.some((s) => (kind === 'orders' ? s.account.orders : s.account.positions).length > 0);
    const what = keptRows ? `rows from ${at}` : `none as of ${at}`;
    return line('stale', help, what, whole ? [code] : kept.flatMap(s => s.account.error ? [s.account.error] : []));
  }
  if (whole) return null;
  if (kind === 'orders' && accounts.some((a) => a.ordersCapped)) {
    return line('incomplete', 'The rail reads a bounded number of saved Polymarket US orders per account; Positions → Polymarket US shows the full list.', 'read limit reached');
  }
  return null;
}

/** The cancel control's place on Polymarket and Polymarket US rows: one fixed
 * width, right-aligned, so ✕ → "Canceling…" → "Requested" (or any other
 * state) never moves the quantity and price beside it. A longer label wraps
 * inside it, within the row's height. */
const RAIL_CANCEL_SLOT = 'inline-flex h-6 w-[4.5rem] shrink-0 items-center justify-end';
/** A worded control in that slot: right-aligned, and wraps rather than spill. */
const RAIL_CANCEL_LABEL = 'inline-flex max-h-6 max-w-full items-center rounded px-0.5 text-right text-[10px] font-medium leading-3';

/** One amber rail line, its quick reference portalled so the rail's scroll
 * box never clips it. */
function RailUsStatusLine({ line, testId, className = '' }: { line: UsRailLine; testId: string; className?: string }) {
  return (
    <div className={`flex items-center gap-1 px-3 py-1 text-[10px] text-amber-500 light:text-amber-700 ${className}`} data-testid={testId}>
      <span className="min-w-0" data-testid="rail-us-line-text" title={line.help}>{line.text}</span>
      <HelpPopover
        label="About this Polymarket US line"
        trigger={<Info size={10} aria-hidden="true" />}
        triggerClassName="inline-flex shrink-0 items-center rounded p-0.5 text-amber-500/80 hover:text-amber-300 light:text-amber-700"
        srDescription={line.help}
        panelWidth={280}
        testId={`${testId}-help`}
      >
        {line.help}
      </HelpPopover>
    </div>
  );
}

type PositionSidebarProps = {
  onClose: () => void;
  /** When provided, the popup title becomes a button that takes the user to
   *  the full Position Tracker tab (and closes the popup). */
  onOpenDetail?: () => void;
  /** Legacy prong collapse (header-only stat line). Superseded on the
   *  positions-page rail by the accordion expand below; kept for API
   *  compatibility with any overlay caller. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Positions-page rail accordions (Brendan, Jul 8): the card is always
   *  content-sized — never taller than its rows need. Resting
   *  (expanded=false) additionally caps the body at RESTING_CAP and
   *  scrolls it internally; expanded uncaps it (no internal scroll — the
   *  rail's stack scroller takes over). When onToggleExpand is provided
   *  the header icon becomes the expand/rest control. */
  expanded?: boolean;
  onToggleExpand?: () => void;
  /** 'overlay' (default): floating panel fixed to the top-right, dismissed on
   *  click-outside / Escape — the Value Dashboard usage. 'docked': renders
   *  inline filling its parent (a sticky aside on the Positions page) as a
   *  content-pushing sidebar — no fixed positioning, resize handle, or
   *  click-outside dismiss. */
  variant?: 'overlay' | 'docked';
  /** Which slice to render. 'all' (default) = the full Value Dashboard
   *  overlay (tracker + recent fills). 'tracker' = P&L + positions + open
   *  orders only. 'fills' = recent fills only. The positions-page rail splits
   *  into two single-section prongs so each docks / pops out / hides on its
   *  own. */
  section?: 'all' | 'tracker' | 'fills';
  /** #1034 — a US read the host already runs (the Positions page shares one
   * with its Cancel all); without it the rail reads on its own. */
  usRail?: UsRail;
};

// A fills-only rail has no position/P&L presentation. Keep the account-wide
// derivation in a separate component so it has no market/fee subscriptions
// there, while tracker and overlay continue using the unchanged real hook.
export function PositionSidebar(props: PositionSidebarProps) {
  return props.section === 'fills'
    ? <PositionSidebarBody {...props} positionPnl={null} />
    : <PositionSidebarWithPnl {...props} />;
}

function PositionSidebarWithPnl(props: PositionSidebarProps) {
  const positionPnl = usePositionPnl();
  return <PositionSidebarBody {...props} positionPnl={positionPnl} />;
}

// Unavailable, never a known empty account. Only fills-only rendering receives
// this value; its JSX excludes every position/summary presentation.
const FILLS_ONLY_PNL: ReturnType<typeof usePositionPnl> = {
  positionsWithPnl: [], totalPnl: null,
  coverage: { valueCents: null, pricedValueCents: 0, pricedPositions: 0, totalPositions: 0, complete: false },
  positionsSync: { synced: false, lastSuccessMs: null, lastErrorMs: null, lastError: null, consecutiveFailures: 0 },
  unpricedSummary: null,
};

function PositionSidebarBody({
  onClose,
  onOpenDetail,
  variant = 'overlay',
  section = 'all',
  collapsed = false,
  onToggleCollapse,
  expanded = false,
  onToggleExpand,
  usRail: sharedUsRail,
  positionPnl,
}: PositionSidebarProps & { positionPnl: ReturnType<typeof usePositionPnl> | null }) {
  const status = useAccountStore((s) => s.status);
  const { phase: kalshiPhase, failure: kalshiFailure } = useKalshiAccountPhase();
  const ordersSync = useAccountStore((s) => s.ordersSync);
  const openOrders = useAccountStore((s) => s.openOrders);
  const balance = useAccountStore((s) => s.balance);
  const balanceHidden = useAccountStore((s) => s.balanceHidden);
  const keyMode = useAccountStore((s) => s.keyMode);
  // Poly twins for the money lines (accountStore/usePositionPnl are
  // Kalshi-only). Gated off the fills prong — it renders neither — and
  // balance additionally off the docked variant (the rail's pinned header
  // band owns the balance readout there; polling twice would be waste).
  // Deliberately NOT gated on the Kalshi connection status: poly data
  // needs no Kalshi, and a Kalshi outage (Thu 3–5am ET maintenance) must
  // not blank the poly numbers (Brendan, Jul-9).
  const polyBalance = usePolyBalance(section !== 'fills' && variant === 'overlay');
  const polyPnl = usePolyUnrealizedPnl(section !== 'fills');
  const polyPnlValue = polyPnl?.valueCents ?? null;
  const polyPnlCoverageTitle =
    polyPnl == null
      ? 'Polymarket unrealized P&L is loading'
      : polyPnl.complete
        ? `${polyPnl.pricedPositions}/${polyPnl.totalPositions} Polymarket positions fully priced`
        : `Polymarket unrealized P&L withheld: ${polyPnl.pricedPositions}/${polyPnl.totalPositions} positions have a live mark, open-fee allocation, and close-fee schedule`;
  // Polymarket open orders (Gitea #49 — poly-first traders read the gap as
  // lost orders). The list fetch is module-shared inside the hook, so this
  // mount rides the SAME 5s poll as PositionTracker's when both are up
  // (docked rail) instead of doubling the relay traffic. Fills prong
  // renders no orders — keep it inert there.
  const polyOrdersState = usePolymarketRelayOrders({ enabled: section !== 'fills' });
  // #1034 — the trader's own open Polymarket US orders, every visible account.
  // Own clients and cadence (useUsRail); US rows never reach executeCancel,
  // which fails closed on any venue it does not know.
  const ownUsRail = useUsRail(section !== 'fills' && !sharedUsRail);
  const usRail = sharedUsRail ?? ownUsRail;
  const usAccounts = usRail.read?.accounts ?? [];
  const usOrders = usAccounts.flatMap((account) => account.orders.map((row) => ({ account, row })));
  const usFailedAccounts = usAccounts.filter((a) => a.error !== null);
  const usCapped = usAccounts.some((a) => a.ordersCapped);
  const usOrdersLineBase = usRailStatusLine('orders', usRail);
  // Rows kept from an earlier read (the whole refresh, or this account's read,
  // failed) are dimmed; the line under them says when they were read.
  const usRowsStale = (account: UsRailAccount) =>
    (usRail.status === 'unavailable' && usRail.read !== null) || account.carriedFromMs !== undefined;
  // Its own path end to end (useUsRailCancel), confirmed like the other rows.
  const usCancel = useUsRailCancel(usRail.refresh);
  // An unconfirmed cancel's next step, visible (its row says "Unconfirmed";
  // the check is in Positions). A failed read's line, when there is one,
  // comes first: it is the bigger unknown.
  const usUnconfirmedCancels = usOrders.filter(({ account, row }) => {
    const c = usRailCancelControl(row, account.readiness, usCancel.local(account.accountId, row.intentId));
    return c.kind === 'status' && c.unconfirmed;
  }).length;
  const usOrdersLine = usOrdersLineBase ?? (usUnconfirmedCancels > 0 ? {
    text: `${usUnconfirmedCancels === 1 ? 'US cancel' : `${usUnconfirmedCancels} US cancels`} unconfirmed · check in Positions`,
    help: 'The cancel request’s result was lost, so the order may still be resting. Check status in Positions → Polymarket US '
      + 'reads the order without resending; the rail never retries a cancel on its own.',
  } : null);
  // Stamped with the login it was opened under: a login or org change closes it.
  const [pendingUsCancelState, setPendingUsCancel] = useState<{ account: UsRailAccount; row: UsOrderRow; identity: string } | null>(null);
  const pendingUsCancel = pendingUsCancelState?.identity === usCancel.identity ? pendingUsCancelState : null;
  // Holdings: same accounts and read as the orders. A failed account's
  // holdings are unknown too, so it is named in both lines.
  const usPositions = usAccounts.flatMap((account) => account.positions.map((row) => ({ account, row })));
  const usPositionGaps = usAccounts.filter((a) => a.error !== null || a.positionsError !== null);
  const usPositionsLine = usRailStatusLine('positions', usRail);
  const usPositionsKnownEmpty = usRail.hasUs === false
    || (usRail.status === 'ready' && usPositionGaps.length === 0 && usPositions.length === 0);
  // "No open orders" is a claim about US too: only once US is known to have
  // none (no US account, or a complete read with nothing open).
  const usKnownEmpty = usRail.hasUs === false
    || (usRail.status === 'ready' && usFailedAccounts.length === 0 && !usCapped && usOrders.length === 0);
  // Every US account's orders were read at some point, but some only by an
  // earlier read (the refresh, or that account's read, failed since): US was
  // checked, so the empty card says the refresh failed, not "not checked".
  const usOrdersOnlyKept = usRail.hasUs === true && usAccounts.length > 0
    && usAccounts.every((a) => a.error === null || a.carriedFromMs !== undefined)
    && (usRail.status === 'unavailable' || usAccounts.some((a) => a.carriedFromMs !== undefined));
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  const replaceOrder = useAccountStore((s) => s.replaceOrder);
  const pinViewerId = useAuthStore((s) => s.user?.id ?? null);
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  // Mirror the positions-page masking (default hidden) so the trade-surface
  // sidebar can't leak the red number the stakeholder asked to hide.
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const { positionsWithPnl, totalPnl, coverage, positionsSync, unpricedSummary } = positionPnl ?? FILLS_ONLY_PNL;
  const kalshiPnlCoverageTitle = kalshiPnlTitle(totalPnl, coverage, positionsSync, unpricedSummary);
  const kalshiPnlText = !positionsSync.synced || positionsSync.consecutiveFailures > 0
    ? '—'
    : totalPnl === null
      ? withheldHeadlineText(coverage, formatDollars)
      : formatDollars(totalPnl);
  const [editOrder, setEditOrder] = useState<SidebarEditOrder | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{
    venue: 'kalshi' | 'polymarket';
    orderId: string;
    label: string;
  } | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // #241 — shared cancel fence (row Cancel/Edit stay disabled while pending).
  // Its timeout notice is KalshiCancelSyncNotice, derived from the same state.
  const kalshiCancelStates = useAccountStore((s) => s.kalshiCancelStates);
  // #748 — the Polymarket half of the same idea. This row used to read no
  // shared state at all: a cancel started from Position Tracker left this ✕
  // looking untouched, and the refusal surfaced in the error box at the bottom
  // of the sidebar rather than on the row that was clicked.
  const polyCancelStates = usePolyCancelStates();
  // Mark-to-market dollar value of all open positions. Falls back to
  // averagePrice when hydra hasn't streamed a current mark yet so the figure
  // stays populated. Mirrors the Position Tracker stat card.
  const positionsValueDollars = positionsWithPnl.reduce((sum, p) => {
    const priceCents = p.currentPrice ?? p.averagePrice;
    return sum + (priceCents * Math.abs(p.position)) / 100;
  }, 0);

  // AudioContext state indicator — poll every 2s
  const [audioState, setAudioState] = useState<string>('suspended');
  useEffect(() => {
    const check = () => setAudioState(getAudioContext()?.state ?? 'unavailable');
    check();
    const id = setInterval(check, 2000);
    return () => clearInterval(id);
  }, []);

  const executeCancel = async (venue: 'kalshi' | 'polymarket', orderId: string) => {
    setCancelError(null);
    if (venue === 'polymarket') {
      // Hook does the optimistic removal + reconcile refresh; it reports
      // failures as a value rather than throwing.
      const out = await polyOrdersState.cancel(orderId);
      if (!out.ok) setCancelError(explainPolyError(out.error));
      return;
    }
    if (venue !== 'kalshi') return unsupportedVenue(venue, 'Cancel');
    try {
      await cancelOrder(orderId);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Cancel failed. Check open orders and try again.';
      setCancelError(message);
    }
  };

  const requestCancel = (venue: 'kalshi' | 'polymarket', orderId: string, label: string) => {
    setCancelError(null);
    if (confirmOrderCancel) {
      setPendingCancel({ venue, orderId, label });
      return;
    }
    void executeCancel(venue, orderId);
  };

  // Resizable width — drag left border, persist to localStorage
  const [width, setWidth] = useState<number>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(SIDEBAR_WIDTH_KEY) : null;
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) && n >= SIDEBAR_WIDTH_MIN && n <= SIDEBAR_WIDTH_MAX ? n : SIDEBAR_WIDTH_DEFAULT;
  });
  const widthRef = useRef(width);
  widthRef.current = width;

  const handleResizePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;

    const onMove = (ev: PointerEvent) => {
      // Anchored on the right, so dragging left (decreasing clientX) makes it wider
      const next = Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, startWidth + (startX - ev.clientX)));
      setWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(widthRef.current));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Dismiss on Escape + click outside the popup. Skipped pointer-down on the
  // resize handle (which lives inside the popup) so resizing doesn't
  // accidentally close it.
  const popupRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The docked sidebar is persistent — only the floating overlay dismisses
    // itself on Escape / click-outside.
    if (variant !== 'overlay') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onPointerDown = (e: PointerEvent) => {
      const node = popupRef.current;
      if (!node) return;
      if (node.contains(e.target as Node)) return;
      if ((e.target as HTMLElement).closest?.('[data-order-actions-menu]')) return;
      // Toolbar toggle button has data-position-toggle so its own click (which
      // already toggles state in the parent) doesn't fire close on top of it.
      if ((e.target as HTMLElement).closest?.('[data-position-toggle]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [onClose, variant]);

  const docked = variant === 'docked';
  const isFills = section === 'fills';
  const kalshiConnected = status === 'connected';
  // #1060: a reconnect (or a failed attempt) after a good read keeps the last
  // Kalshi rows, as the Tracker does (#904): they used to vanish for every
  // reconnect. A disconnect (sign-out, switch) clears them in the store.
  const kalshiRowsShown = kalshiConnected
    || ((status === 'connecting' || status === 'error') && positionsSync.lastSuccessMs !== null);
  // The header count is a claim about Kalshi only once a read answered.
  const kalshiPositionsCount = positionsSync.synced
    ? `${positionsWithPnl.length} / ${groupedDollars(positionsValueDollars)} (K)`
    : '— (K)';
  // "No open orders" is a claim about every venue: Kalshi (an orders read
  // answered with none, or no keys at all), Polymarket (answered) and US
  // (usKnownEmpty). Kalshi orders are known from the orders read itself
  // (`ordersSync`), not from the positions read. A disconnected account with
  // keys still has its orders at Kalshi: not empty, unknown.
  const polyOrdersSettled = polyOrdersState.status === 'ready' || polyOrdersState.status === 'unavailable';
  const kalshiOrdersKnown = ordersSync.lastSuccessMs !== null;
  const kalshiOrdersKnownEmpty = (kalshiRowsShown && kalshiOrdersKnown && openOrders.length === 0)
    || kalshiPhase === 'absent';
  const ordersPending = kalshiPhase === 'checking'
    || (kalshiPhase === 'connecting' && !kalshiOrdersKnown)
    || (kalshiPhase === 'connected' && !kalshiOrdersKnown && ordersSync.consecutiveFailures === 0)
    || polyOrdersState.status === 'idle' || polyOrdersState.status === 'loading';
  return (
    <div
      ref={popupRef}
      className={
        docked
          ? // Content-sized, never stretched: the card ends where its rows
            // end (a tall monitor gets open rail below, not padded cards).
            // Full-bleed hard dock (Brendan, Jul 8): square corners, no
            // x-borders — the rail's border-l is the only vertical line;
            // border-y separates the card from the rail background.
            'bg-card border-y flex flex-col overflow-hidden border-gray-800'
          : 'bg-card fixed right-3 top-3 z-40 flex max-h-[calc(100vh-1.5rem)] flex-col rounded-lg border border-gray-800 shadow-2xl'
      }
      style={docked ? undefined : { width: `${width}px` }}
    >
      {/* Drag handle on the left edge — overlay only (docked width = the aside's) */}
      {!docked && (
        <div
          onPointerDown={handleResizePointerDown}
          className="absolute left-0 top-0 bottom-0 z-10 w-1 cursor-col-resize hover:bg-blue-500/40"
          title="Drag to resize"
        />
      )}
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-800 px-3 py-2.5">
        {/* #1034: wraps rather than pushing balances past a narrow pop-up's edge
            (as the docked band does); each label stays with its amount. */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
          {isFills ? (
            <>
              {onToggleExpand ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  className={`flex items-center gap-0.5 rounded p-0.5 transition-colors hover:bg-gray-800 ${
                    expanded ? 'text-primary hover:text-primary' : 'text-gray-400 hover:text-gray-200'
                  }`}
                  title={expanded ? 'Cap recent fills (scrolls inside)' : 'Expand recent fills to full height'}
                >
                  <Receipt size={14} />
                  {expanded ? <FoldVertical size={11} /> : <UnfoldVertical size={11} />}
                </button>
              ) : onToggleCollapse ? (
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  aria-expanded={!collapsed}
                  className="flex items-center gap-0.5 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"
                  title={collapsed ? 'Expand recent fills' : 'Collapse recent fills'}
                >
                  <Receipt size={14} />
                  <ChevronDown
                    size={11}
                    className={`transition-transform ${collapsed ? '-rotate-90' : ''}`}
                  />
                </button>
              ) : (
                <Receipt size={14} className="text-gray-400" />
              )}
              <span className="text-sm font-medium text-gray-300" title="Kalshi and attributed Polymarket fills">
                Recent Fills
              </span>
            </>
          ) : (
            <>
              {onToggleExpand ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  className={`flex items-center gap-0.5 rounded p-0.5 transition-colors hover:bg-gray-800 ${
                    expanded ? 'text-primary hover:text-primary' : 'text-gray-400 hover:text-gray-200'
                  }`}
                  title={expanded ? 'Cap the tracker (scrolls inside)' : 'Expand the tracker to full height'}
                >
                  <Crosshair size={14} />
                  {expanded ? <FoldVertical size={11} /> : <UnfoldVertical size={11} />}
                </button>
              ) : onToggleCollapse ? (
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  aria-expanded={!collapsed}
                  className="flex items-center gap-0.5 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"
                  title={collapsed ? 'Expand the tracker' : 'Collapse the tracker'}
                >
                  <Crosshair size={14} />
                  <ChevronDown
                    size={11}
                    className={`transition-transform ${collapsed ? '-rotate-90' : ''}`}
                  />
                </button>
              ) : (
                <Crosshair size={14} className="text-gray-400" />
              )}
              {onOpenDetail ? (
                <button
                  onClick={onOpenDetail}
                  className="text-sm font-medium text-gray-300 transition-colors hover:text-primary hover:underline"
                  title="Open Position Tracker — count/value are Kalshi; Polymarket positions live on the Positions page"
                >
                  Positions{' '}
                  <span className="text-gray-500">
                    {kalshiPositionsCount}
                  </span>
                </button>
              ) : (
                <span
                  className="text-sm font-medium text-gray-300"
                  title="Count/value are Kalshi; Polymarket positions live on the Positions page"
                >
                  Positions{' '}
                  <span className="text-gray-500">
                    {kalshiPositionsCount}
                  </span>
                </span>
              )}
              {/* Balances render in the OVERLAY header only — the docked
                  rail shows them in its pinned header band instead, so the
                  prong title row stays one line (Brendan, Jul 9). This
                  header branch only renders for the tracker/'all' prongs
                  (the fills prong takes the isFills branch above). */}
              {/* #1060: K and P hold their place with "—" until read, as the
                  docked band does; they used to pop in and re-wrap this header,
                  pushing the panel and its close button down. */}
              {!docked && (
                <span className="whitespace-nowrap text-xs text-gray-500" title={kalshiReportedBalanceTitle(keyMode)}>
                  Bal K{' '}
                  <span className="text-gray-300 light:text-gray-800" data-testid="overlay-balance-kalshi"
                    title={balanceHidden ? ORG_BALANCE_HIDDEN_TITLE : undefined}>
                    {balanceHidden ? ORG_BALANCE_HIDDEN_LABEL : balance !== null
                      ? `$${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '—'}
                  </span>
                </span>
              )}
              {!docked && (
                <span
                  className="whitespace-nowrap text-xs text-gray-500"
                  title={polyBalance.source === 'org' ? 'Polymarket org account (funder USDC)'
                    : polyBalance.source === 'personal' ? 'Polymarket personal account (funder USDC)'
                      : 'Polymarket balance — no org poly keys and no personal connection'}
                >
                  P{' '}
                  <span className="text-gray-300 light:text-gray-800" data-testid="overlay-balance-poly"
                    title={polyBalance.hidden ? ORG_BALANCE_HIDDEN_TITLE : undefined}>
                    {polyBalance.hidden ? ORG_BALANCE_HIDDEN_LABEL : polyBalance.usd !== null
                      ? `$${polyBalance.usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '—'}
                  </span>
                </span>
              )}
              {!docked && <UsBalanceSlot rail={usRail} className="text-xs text-gray-500" valueClassName="text-gray-300 light:text-gray-800" />}
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                  audioState === 'running' ? 'bg-green-500' : audioState === 'suspended' ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                title={`Audio: ${audioState}`}
              />
            </>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-800 hover:text-gray-300"
            title={docked ? 'Hide this panel' : 'Close'}
            aria-label={docked ? 'Hide this panel' : 'Close'}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {!collapsed && (
      <div
        className={
          onToggleExpand
            ? expanded
              ? // Expanded accordion: full content height, no internal
                // scroll — the rail's stack scroller handles overflow.
                ''
              : // Resting accordion: capped at a real, usable height
                // (max-h-56 = 224px ≈ 6-8 fill rows) and scrolls inside.
                // Under the cap the card just ends at its content.
                'max-h-56 overflow-y-auto'
            : 'flex-1 overflow-y-auto'
        }
      >
        {/* Kalshi gate is a BANNER, not a body replacement — the poly money
            lines and poly order rows ride their own relay and must not blank
            during a Kalshi outage (Brendan, Jul-9; this gate's own copy says
            "Polymarket is unaffected"). Kalshi-sourced sections below gate
            individually instead. */}
        {/* #1060: only a settled answer is named. While the keys are checked or
            the account connects, nothing is claimed (the Kalshi figures read
            "—"); a failed check is not "no keys" and offers its Retry. */}
        {(kalshiPhase === 'absent' || kalshiPhase === 'not-connected') && (
          <div className="px-3 py-4 text-center text-xs text-gray-500">
            Connect Kalshi in Settings
            <div className="mt-1 text-[10px]">
              (Kalshi maintenance runs Thu 3–5 AM ET; Polymarket is unaffected)
            </div>
          </div>
        )}
        {kalshiPhase === 'check-failed' && (
          <div className="px-3 py-4 text-center text-xs text-gray-500" data-testid="rail-kalshi-check-failed">
            {KALSHI_PHASE_TEXT['check-failed']}{' '}
            <button
              type="button"
              onClick={() => { void useAccountStore.getState().checkCredentials(); }}
              className="border-border hover:bg-muted rounded border px-1.5 text-xs"
            >
              Retry
            </button>
          </div>
        )}
        {kalshiPhase === 'unavailable' && (
          <div className="px-3 py-4 text-center text-xs text-gray-500" title={kalshiFailure ?? undefined}>
            {KALSHI_PHASE_TEXT.unavailable}
            <div className="mt-1 text-[10px]">
              (Kalshi maintenance runs Thu 3–5 AM ET; Polymarket is unaffected)
            </div>
          </div>
        )}
        <>
            {section !== 'fills' && (
            <>
            {/* P&L Summary — venue-split: usePositionPnl is Kalshi-only, and
                an unlabeled total reads as "the account". Poly rides its own
                hook (same formula as the tracker's headline tile). */}
            <div className="flex items-center gap-3 border-b border-gray-800/50 px-3 py-2.5">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-gray-500" title={MTM_PNL_TOOLTIP}>
                  Unrealized P&L · MTM
                </div>
                <div className="flex flex-wrap items-baseline gap-1.5">
                  <span className="text-[10px] text-gray-500" title={showPnl ? kalshiPnlCoverageTitle : 'Kalshi unrealized P&L'}>K</span>
                  <span
                    className={`text-sm font-semibold ${
                      !showPnl || totalPnl === null ? 'text-gray-500' : totalPnl >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}
                    title={showPnl ? kalshiPnlCoverageTitle : 'P&L hidden — toggle on the Positions page'}
                  >
                    {!positionsSync.synced ? '—' : showPnl ? kalshiPnlText : '•••'}
                  </span>
                  <span
                    className="text-[10px] text-gray-500"
                    title={polyPnlCoverageTitle}
                  >
                    P
                  </span>
                  <span
                    className={`text-sm font-semibold ${
                      !showPnl || polyPnlValue === null
                        ? 'text-gray-500'
                        : polyPnlValue >= 0
                          ? 'text-green-400'
                          : 'text-red-400'
                    }`}
                    title={
                      showPnl
                        ? polyPnlCoverageTitle
                        : 'P&L hidden — toggle on the Positions page'
                    }
                  >
                    {!showPnl
                      ? '•••'
                      : polyPnlValue != null
                        ? formatDollars(polyPnlValue)
                        : polyPnl == null
                          ? '—'
                          : `— (${polyPnl.pricedPositions}/${polyPnl.totalPositions})`}
                  </span>
                  <UsRailUnrealized rail={usRail} showPnl={showPnl} />
                </div>
                <PositionsRefreshWarning positionsSync={positionsSync} showPnl={showPnl} title={kalshiPnlCoverageTitle} />
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-gray-500" title="Kalshi positions / open orders">Pos / Ord (K)</div>
                <div className="text-sm text-gray-300">
                  {/* #1060: each figure only once its own read answered. */}
                  {kalshiRowsShown
                    ? `${positionsSync.synced ? positionsWithPnl.length : '—'} / ${kalshiOrdersKnown ? openOrders.length : '—'}`
                    : '—'}
                </div>
              </div>
            </div>

            {/* Positions List (Kalshi-sourced — gated on SNAPSHOT evidence, so a
                transient reconnect keeps the holdings on screen and an
                unreadable snapshot never reads as "No open positions"). */}
            {positionsSync.synced && positionsWithPnl.length > 0 && (
              <div className="border-b border-gray-800/50">
                {positionsWithPnl.map((pos) => {
                  // #964: an NFL ladder prop's badge carries the player and rung; the
                  // stat rides whole ahead of the matchup (see MarketTypeLeadText).
                  const { marketLabel, betTeam: fullBetTeam, badgeTeam, marketType } = describeMarket(pos.ticker, pos.side);
                  const betTeam = badgeTeam ?? fullBetTeam;
                  return (
                  <div
                    key={`${pos.ticker}-${pos.side}`}
                    className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-800/30"
                  >
                    <div className={`flex items-center gap-1.5 overflow-hidden ${marketType ? 'flex-wrap' : ''}`}>
                      <span className={`${marketType ? 'max-w-full' : 'max-w-[75%]'} shrink-0 rounded px-1 py-0.5 text-[9px] font-bold [overflow-wrap:anywhere] ${
                        pos.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {betTeam ? `(${betTeam}) ` : ''}{pos.side === 'yes' ? 'Y' : 'N'}
                      </span>
                      <MarketTypeLeadText marketType={marketType} marketLabel={marketLabel} title={pos.ticker} />
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-gray-500">{fmtQty(pos.position)}</span>
                      <span className="text-gray-500">@{formatCents(pos.averagePrice)}</span>
                      {pos.unrealizedPnl !== null ? (
                        <span className={`w-14 text-right font-mono ${
                          !showPnl ? 'text-gray-500' : pos.unrealizedPnl >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {showPnl ? formatDollars(pos.unrealizedPnl) : '•••'}
                        </span>
                      ) : (
                        <span className="w-14 text-right text-gray-600">&mdash;</span>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* #1034 — the trader's US holdings, every visible account. G1's
                attributed ledger quantity and complete-history average; no live
                mark is requested by this rail, so no P&L is shown, and
                a holding whose size is unknown reads as unknown, never 0. */}
            {usPositions.length > 0 && (
              <div className="border-b border-gray-800/50" data-testid="rail-us-positions">
                {usPositions.map(({ account, row }) => {
                  const view = presentUsPosition(row);
                  const economics = usPositionEconomics(row);
                  const held = view.held === UNAVAILABLE ? '—' : view.held;
                  const stale = usRowsStale(account);
                  return (
                    <div
                      key={`us-${account.accountId}-${view.key}`}
                      data-testid="rail-us-position"
                      data-stale={stale || undefined}
                      className={`flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-800/30 ${stale ? 'opacity-60' : ''}`}
                      title={`${view.complete ? 'G1 attributed holding' : `Partly known — ${view.reasons.join(', ') || 'holding not established'}`} · account …${account.accountId.slice(-4)}`}
                    >
                      <div className="flex items-center gap-1.5 overflow-hidden">
                        <VenueChip venue="polymarket_us" />
                        <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                          row.target.outcome === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {row.target.outcome === 'yes' ? 'Y' : 'N'}
                        </span>
                        <span className="truncate text-gray-300 light:text-gray-800" title={row.target.marketSlug}>{usMarketLabel(row.target).label}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={view.complete ? 'text-gray-500' : 'text-amber-500 light:text-amber-700'}
                          title={held === '—' ? 'Holding size unknown' : view.complete ? 'G1 held contracts'
                            : `* Partly known — ${view.reasons.join(', ') || 'holding not established'}`}>
                          {held}{view.complete ? '' : '*'}
                        </span>
                        <span className="w-14 text-right text-gray-500" title={economics.averageCents === null ? economics.reason ?? 'Average entry unavailable' : 'Average entry price before fees'}>
                          {economics.averageCents === null ? '—' : `${Number(economics.averageCents.toFixed(2))}¢`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {usPositionsLine && (
              <RailUsStatusLine line={usPositionsLine} testId="rail-us-positions-line" className="border-b border-gray-800/50" />
            )}

            {positionsSync.synced && positionsWithPnl.length === 0 && usPositions.length === 0 && usPositionsKnownEmpty && (
              <div className="border-b border-gray-800/50 px-3 py-4 text-center text-xs text-gray-500">
                No open positions
              </div>
            )}
            {kalshiConnected && !positionsSync.synced && (
              <div
                className="border-b border-gray-800/50 px-3 py-4 text-center text-xs text-amber-500"
                title={showPnl ? kalshiPnlCoverageTitle : undefined}
              >
                Positions unavailable
              </div>
            )}

            {/* Open Orders — every venue, venue-chipped like the positions
                page's unified tables. Kalshi rows keep edit + cancel; poly
                and US rows are display + cancel (price edits live on the
                Positions page; US cancels take their own path, #1034). */}
            <div className="border-b border-gray-800/50">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-500">
                Open Orders
              </div>
              {kalshiRowsShown && openOrders.map((ord) => {
                const { marketLabel, betTeam: fullBetTeam, badgeTeam, marketType } = describeMarket(ord.ticker, ord.side);
                const betTeam = badgeTeam ?? fullBetTeam;
                // Kalshi often returns only yes_price even for NO orders; for
                // a NO bid the actual price is 100 - yes_price.
                const sidePrice =
                  ord.side === 'yes'
                    ? (ord.yesPrice ?? (ord.noPrice != null ? 100 - ord.noPrice : null))
                    : (ord.noPrice ?? (ord.yesPrice != null ? 100 - ord.yesPrice : null));
                return (
                <div
                  key={ord.orderId}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-800/30"
                >
                  <div className={`flex items-center gap-1.5 overflow-hidden ${marketType ? 'flex-wrap' : ''}`}>
                    <VenueChip venue="kalshi" />
                    <span className={`${marketType ? 'max-w-full' : 'max-w-[75%]'} shrink-0 rounded px-1 py-0.5 text-[9px] font-bold [overflow-wrap:anywhere] ${
                      ord.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {betTeam ? `(${betTeam}) ` : ''}{ord.side === 'yes' ? 'Y' : 'N'}
                    </span>
                    <MarketTypeLeadText marketType={marketType} marketLabel={marketLabel} title={ord.ticker} />
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-gray-500">{ord.remainingCount}</span>
                    <span className="text-gray-500">
                      @{sidePrice != null ? formatCents(sidePrice) : '—'}
                    </span>
                    <span className={`rounded px-1 py-0.5 text-[9px] font-medium ${
                      ord.type === 'limit' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {ord.type === 'limit' ? 'Lim' : 'Mkt'}
                    </span>
                    <OrderActionButtons
                      // #241: this row previously passed no `canceling` at all,
                      // so its Cancel stayed live through the whole request —
                      // the worst of the three surfaces for double-clicking.
                      canceling={isKalshiCancelPending(kalshiCancelStates[ord.orderId])}
                      onCancel={() =>
                        requestCancel(
                          'kalshi',
                          ord.orderId,
                          ticketLabel(marketLabel, fullBetTeam),
                        )
                      }
                      onEdit={() => {
                        setEditOrder({
                          orderId: ord.orderId,
                          marketLabel: ticketLabel(marketLabel, fullBetTeam),
                          side: ord.side,
                          action: ord.action,
                          remainingCount: ord.remainingCount,
                          currentPriceCents: sidePrice ?? 50,
                          ticker: ord.ticker,
                        });
                      }}
                      editAvailable={ord.reconciliation?.actions.decrease !== 'unavailable'}
                      className="ml-0.5"
                    />
                  </div>
                </div>
                );
              })}
              {polyOrdersState.orders.map((o, i) => {
                const id = String(o.id ?? o.orderID ?? o.orderId ?? '');
                const conditionId = String(o.market ?? '');
                // Market-level label (orientation-free): the CLOB row's
                // BUY/SELL says bid vs ask; the outcome-token side chip the
                // tracker shows needs the token desc and isn't worth a
                // sweep here.
                const label =
                  describePolyMarketCached(conditionId)?.matchup ??
                  (conditionId.length > 14
                    ? `${conditionId.slice(0, 6)}…${conditionId.slice(-4)}`
                    : conditionId || id);
                const isSell = String(o.side ?? '').toUpperCase() === 'SELL';
                const priceFrac =
                  typeof o.price === 'number' ? o.price : Number(o.price ?? NaN);
                // Centi-cent grain — a resting 58.25¢ CLOB order must not
                // display as 58¢ (sub-cent contract).
                const priceCents = Number.isFinite(priceFrac)
                  ? Math.round(priceFrac * 10000) / 100
                  : null;
                // Shared derivation with the hook's replace flow; CLOB
                // sizes are fractional — round for display.
                const remainingRaw = polyOpenOrderRemaining(o);
                const remaining = remainingRaw != null ? Math.round(remainingRaw) : null;
                // #748 — the one shared answer, so this row agrees with every
                // other mount showing the same order.
                const cancelRow = describePolyCancelRow(polyCancelStates[id]);
                return (
                  <div
                    key={id ? `poly-${id}` : `poly-row-${i}`}
                    className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-800/30"
                  >
                    <div className="flex items-center gap-1.5 overflow-hidden">
                      <VenueChip venue="polymarket" />
                      <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                        isSell ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                      }`}>
                        {isSell ? 'ASK' : 'BID'}
                      </span>
                      <span className="truncate text-gray-300 light:text-gray-800" title={conditionId || id}>
                        {label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-gray-500">{remaining ?? '—'}</span>
                      <span className="text-gray-500">
                        @{priceCents != null ? formatCents(priceCents) : '—'}
                      </span>
                      {/* An id-less row can't be cancelled — a bare id would
                          DELETE /api/polymarket/orders/ (safe 404, but noise).
                          The slot stays either way, so rows line up. */}
                      <span className={RAIL_CANCEL_SLOT} data-testid="rail-cancel-slot">
                      {id !== '' && (
                        <button
                          type="button"
                          onClick={() =>
                            // Full order identity in the confirm — two resting
                            // orders on the same market must be tellable apart.
                            requestCancel(
                              'polymarket',
                              id,
                              `${label} ${isSell ? 'ASK' : 'BID'} ${remaining ?? '—'} @ ${
                                priceCents != null ? `${priceCents}¢` : '—'
                              }`,
                            )
                          }
                          disabled={cancelRow.pending}
                          title={cancelRow.message ?? 'Cancel order'}
                          aria-label={cancelRow.message ?? 'Cancel Polymarket order'}
                          className={
                            cancelRow.label
                              ? `${RAIL_CANCEL_LABEL} ${
                                  // Amber is this sidebar's established
                                  // could-not-confirm tone (the #241 notice
                                  // below uses it); a pending row stays neutral
                                  // because nothing has gone wrong yet.
                                  cancelRow.unconfirmed
                                    ? 'text-amber-300 hover:bg-amber-500/10'
                                    : 'text-gray-400'
                                } disabled:cursor-default`
                              : 'inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover:bg-red-500/10 hover:text-red-400'
                          }
                        >
                          {cancelRow.label ?? <X size={14} />}
                        </button>
                      )}
                      </span>
                    </div>
                  </div>
                );
              })}
              {usOrders.map(({ account, row }) => {
                const view = presentUsOrder(row);
                const yes = row.target.outcome === 'yes';
                const remaining = view.remaining === UNAVAILABLE ? '—' : view.remaining;
                const control = usRailCancelControl(row, account.readiness, usCancel.local(account.accountId, row.intentId));
                const stale = usRowsStale(account);
                return (
                  <div
                    key={`us-${account.accountId}-${row.intentId}`}
                    data-testid="rail-us-order"
                    data-stale={stale || undefined}
                    className={`flex flex-wrap items-center justify-between gap-x-2 gap-y-1 px-3 py-1.5 text-xs hover:bg-gray-800/30 ${stale ? 'opacity-60' : ''}`}
                    title={`${view.status}${view.fillStatus ? ` · ${view.fillStatus}` : ''}${view.statusReason ? ` — ${view.statusReason}` : ''} · account …${account.accountId.slice(-4)}`}
                  >
                    <div className="flex min-w-24 flex-1 basis-24 items-center gap-1.5 overflow-hidden">
                      <VenueChip venue="polymarket_us" />
                      <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                        yes ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {row.action.toUpperCase()} {row.target.outcome.toUpperCase()}
                      </span>
                      <span className="truncate text-gray-300 light:text-gray-800" title={row.target.marketSlug}>
                        {usMarketLabel(row.target).label}
                      </span>
                    </div>
                    <div className="ml-auto flex max-w-full shrink-0 flex-wrap items-center gap-1.5">
                      {/* Exact saved text: US sizes and prices are decimals, never rounded. */}
                      <span className="text-gray-500" title={usOrderNeedsFillVisibility(row) && view.terminal ? 'Filled contracts awaiting holdings confirmation' : remaining === '—' ? view.status : 'Remaining contracts'}>{usOrderNeedsFillVisibility(row) && view.terminal ? view.filled : remaining}</span>
                      <span className="text-gray-500">@{usRailPrice(row.outcomePrice)}</span>
                      <span className={RAIL_CANCEL_SLOT} data-testid="rail-cancel-slot">
                      {!view.terminal && (control.kind === 'status' ? (
                        <span
                          data-testid="rail-us-cancel-status"
                          title={control.title}
                          className={`${RAIL_CANCEL_LABEL} ${
                            control.unconfirmed ? 'text-amber-300 light:text-amber-700'
                              : control.blocked ? 'text-gray-500' : 'text-gray-400'
                          }`}
                        >
                          {control.label}
                        </span>
                      ) : control.kind !== 'none' && (
                        <button
                          type="button"
                          data-testid="rail-us-cancel"
                          onClick={() => {
                            if (confirmOrderCancel) setPendingUsCancel({ account, row, identity: usCancel.identity });
                            else void usCancel.cancel(account, row);
                          }}
                          title={control.title}
                          aria-label={control.title}
                          className={control.label
                            ? `${RAIL_CANCEL_LABEL} text-amber-300 light:text-amber-700 hover:bg-amber-500/10`
                            : 'inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover:bg-red-500/10 hover:text-red-400'}
                        >
                          {control.label ?? <X size={14} />}
                        </button>
                      ))}
                      </span>
                    </div>
                    {usOrderNeedsFillVisibility(row) && (
                      <div data-testid="rail-us-fill-status" className="basis-full text-[10px] text-amber-300 light:text-amber-700">
                        {view.fillStatus} · {view.filled} filled{!view.terminal ? ` · ${remaining} left` : ''}
                      </div>
                    )}
                  </div>
                );
              })}
              {usOrdersLine && <RailUsStatusLine line={usOrdersLine} testId="rail-us-orders-line" />}
              {(!kalshiRowsShown || openOrders.length === 0) && polyOrdersState.orders.length === 0 && usOrders.length === 0 && (
                // One line in every state, so the card keeps its height as the reads answer.
                <div className="px-3 py-2 text-center text-xs text-gray-600" data-testid="rail-orders-empty">
                  {kalshiOrdersKnownEmpty && polyOrdersSettled && usKnownEmpty ? 'No open orders'
                    : ordersPending || usRail.status === 'loading' ? 'Reading orders…'
                      // US failed or is unknown: its own line above names it, and
                      // this one never reads as "no orders" without it.
                      : kalshiOrdersKnownEmpty && polyOrdersSettled
                        ? `No Kalshi or Polymarket (intl) orders · ${usCapped && usFailedAccounts.length === 0 && usRail.status !== 'unavailable'
                          ? 'US not fully read' : usOrdersOnlyKept ? 'US refresh failed' : 'US not checked'}`
                        : 'Orders unavailable'}
                </div>
              )}
            </div>
            </>
            )}

            {/* Recent Fills. As its own rail prong (section 'fills') the prong
                header carries the title, so the inline sub-header + /fills
                pop-out button is only for the 'all' overlay. */}
            {/* NOT gated on kalshiConnected (#457): the tape is two-venue now,
                so a Kalshi outage must not blank attributed Polymarket fills —
                same rule the poly money lines and order rows already follow.
                ActivityTicker owns its own empty/unavailable states. */}
            {section !== 'tracker' && (
              isFills ? (
                // px-1 so the fill rows' content lines up with the tracker
                // prong's px-3 rows (ActivityTicker rows are px-2; the +4px
                // wrapper matches — same as the 'all' overlay below).
                <div className="px-1 py-1">
                  <ActivityTicker />
                </div>
              ) : (
                <div className="px-1 py-1">
                  <div className="flex items-center justify-between px-2 py-1">
                    <span className="text-[10px] uppercase tracking-wider text-gray-500">
                      Recent Fills
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        // Named window so re-clicking the button focuses the
                        // existing popup instead of spawning a duplicate.
                        window.open(
                          '/fills',
                          'g1-fills-popup',
                          'width=420,height=640,toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes',
                        );
                      }}
                      className="text-gray-500 hover:bg-gray-700/50 hover:text-gray-300 rounded p-0.5 transition-colors"
                      title="Open in a separate window"
                      aria-label="Pop out recent fills"
                    >
                      <ExternalLink size={10} />
                    </button>
                  </div>
                  <ActivityTicker />
                </div>
              )
            )}
        </>
      </div>
      )}

      {cancelError && (
        <div className="mx-3 mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
          <div className="flex items-start justify-between gap-2">
            <span>{cancelError}</span>
            <button
              type="button"
              onClick={() => setCancelError(null)}
              className="text-red-300/70 hover:text-red-200"
              aria-label="Dismiss cancel error"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      {usCancel.error && (
        <div className="mx-3 mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400" data-testid="rail-us-cancel-error">
          <div className="flex items-start justify-between gap-2">
            <span className="[overflow-wrap:anywhere]">{usCancel.error}</span>
            <button
              type="button"
              onClick={usCancel.dismissError}
              className="text-red-300/70 hover:text-red-200"
              aria-label="Dismiss Polymarket US cancel error"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Separate from cancelError so an immediate failure and a stalled
          synchronization can't mask one another (#241). Outside the scroll
          area so it stays in view; compact and bounded (#1357). Not on the
          fills-only prong: the Positions page's tracker prong already carries
          it, and fills have no Cancel. */}
      {!isFills && <KalshiCancelSyncNotice className="mx-3 mb-3" />}

      <ConfirmModal
        isOpen={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        onConfirm={() => {
          // Clear the captured target before firing so the modal can't
          // re-submit; the store's duplicate guard is the final safety net.
          const target = pendingCancel;
          setPendingCancel(null);
          if (target) void executeCancel(target.venue, target.orderId);
        }}
        title="Cancel order?"
        message={pendingCancel ? `Cancel ${pendingCancel.label}?` : 'Cancel this order?'}
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />

      {/* #1034 — US has its own confirmation so its target can never reach
          executeCancel. The account tail tells same-market rows apart. */}
      <ConfirmModal
        isOpen={pendingUsCancel !== null}
        onClose={() => setPendingUsCancel(null)}
        onConfirm={() => {
          const target = pendingUsCancel;
          setPendingUsCancel(null);
          if (target) void usCancel.cancel(target.account, target.row, target.identity);
        }}
        title="Cancel Polymarket US order?"
        message={pendingUsCancel
          ? usCancelConfirmMessage(pendingUsCancel.account.accountId, pendingUsCancel.row)
          : 'Cancel this order?'}
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />

      {editOrder && (
        <EditOrderPriceModal
          isOpen={editOrder !== null}
          onClose={() => setEditOrder(null)}
          onReplace={async (newPriceCents, newQuantity, options) => {
            // accountStore.replaceOrder smart-routes by default: same-price
            // qty-shrink goes through /decrease (queue-preserving); anything
            // else is cancel + new-place. The modal's force-cancel toggle
            // forwards as options.forceCancelReplace, overriding the smart
            // route when set. Sidebar only renders Kalshi orders today.
            // A cancel+place edit mints a new id; the pin (#943) follows it.
            await editOrderKeepingPin(
              'kalshi',
              editOrder.orderId,
              () => replaceOrder(editOrder.orderId, newPriceCents, newQuantity, options?.forceCancelReplace),
              pinViewerId,
            );
            setEditOrder(null);
          }}
          marketLabel={editOrder.marketLabel}
          side={editOrder.side}
          action={editOrder.action}
          remainingCount={editOrder.remainingCount}
          currentPriceCents={editOrder.currentPriceCents}
          ticker={editOrder.ticker}
          venue="kalshi"
        />
      )}
    </div>
  );
}

// ── PositionsWidget — compact toolbar-height readout + popup toggle ──

/**
 * Lightweight P&L / counts readout that sits in the toolbar row, with the
 * crosshair as the expand control for the floating popup. Sized via
 * `self-stretch` on a flex parent so its vertical extent matches the
 * adjacent toolbar.
 */
export function PositionsWidget({
  expanded,
  onToggle,
  usRail,
}: {
  expanded: boolean;
  onToggle: () => void;
  usRail?: UsRail;
}) {
  // Hosts with a rail share it; other hosts expose unknown rather than starting
  // a second account poll. This presence read only consults the existing cache.
  const usPresence = useUsAccountPresence(false);
  const usSummary = usRail ?? { hasUs: usPresence, status: 'off' as const, read: null, error: null };
  const usDescription = useId();
  const openOrders = useAccountStore((s) => s.openOrders);
  const balance = useAccountStore((s) => s.balance);
  const balanceHidden = useAccountStore((s) => s.balanceHidden);
  const keyMode = useAccountStore((s) => s.keyMode);
  const { phase: kalshiPhase } = useKalshiAccountPhase();
  const { positionsWithPnl, totalPnl, coverage, positionsSync, unpricedSummary } = usePositionPnl();
  const kalshiPnlCoverageTitle = kalshiPnlTitle(totalPnl, coverage, positionsSync, unpricedSummary);
  const kalshiPnlText = !positionsSync.synced || positionsSync.consecutiveFailures > 0
    ? '—'
    : totalPnl === null
      ? withheldHeadlineText(coverage, formatDollars)
      : formatDollars(totalPnl);
  // Venue-split money lines: usePositionPnl/accountStore are KALSHI-only,
  // and rendering them unlabeled reads as "the account". Poly twins ride
  // their own hooks (org-key relay balance with personal fallback; poly
  // unrealized from the same formula as the tracker tile). NOT gated on
  // the Kalshi connection — a Kalshi outage must not blank poly numbers.
  const polyBalance = usePolyBalance();
  const polyPnl = usePolyUnrealizedPnl();
  const polyPnlValue = polyPnl?.valueCents ?? null;
  const polyPnlCoverageTitle =
    polyPnl == null
      ? 'Polymarket unrealized P&L is loading'
      : polyPnl.complete
        ? `${polyPnl.pricedPositions}/${polyPnl.totalPositions} Polymarket positions fully priced`
        : `Polymarket unrealized P&L withheld: ${polyPnl.pricedPositions}/${polyPnl.totalPositions} positions have a live mark, open-fee allocation, and close-fee schedule`;
  const contractCount = positionsWithPnl.reduce((sum, p) => sum + Math.abs(p.position), 0);
  // Mark-to-market dollar value across all open positions; falls back to
  // averagePrice when hydra hasn't streamed a current mark yet so the figure
  // stays populated. Mirrors the Position Tracker stat-card calculation.
  const contractsValueDollars = positionsWithPnl.reduce((sum, p) => {
    const priceCents = p.currentPrice ?? p.averagePrice;
    return sum + (priceCents * Math.abs(p.position)) / 100;
  }, 0);
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const pnlClass = !showPnl || totalPnl === null
    ? 'text-muted-foreground'
    : totalPnl >= 0
      ? 'text-positive'
      : 'text-negative';

  return (
    <button
      data-position-toggle
      onClick={onToggle}
      title={expanded ? 'Hide positions' : 'Show positions'}
      aria-label={expanded ? 'Hide positions popup' : 'Show positions popup'}
      aria-describedby={usSummary.hasUs === false ? undefined : `${usDescription}-pnl ${usDescription}-balance`}
      className={cn(
        'flex h-full w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
        expanded ? 'bg-primary/10' : 'hover:bg-muted/50',
      )}
    >
      {/* #1060: the same cell names why there are no Kalshi figures yet. A key
          check still running, or one that failed, is not "Not connected". */}
      {!positionsSync.synced && kalshiPhase !== 'connected' ? (
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-muted-foreground text-xs" data-testid="positions-widget-status">
            {kalshiPhase === 'absent' || kalshiPhase === 'not-connected' ? 'Not connected' : KALSHI_PHASE_TEXT[kalshiPhase]}
          </span>
          {usSummary.hasUs !== false && <span className="flex flex-wrap items-baseline gap-1.5">
            <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">Unrealized P&amp;L · MTM</span>
            <UsRailUnrealized rail={usSummary} showPnl={showPnl} insideButton descriptionId={`${usDescription}-pnl`} />
          </span>}
          <UsBalanceSlot rail={usSummary} focusable={false} descriptionId={`${usDescription}-balance`} className="text-muted-foreground"
            valueClassName="text-foreground font-mono text-sm font-semibold" />
        </span>
      ) : (
        <div className="flex flex-col justify-center gap-0.5">
          <div className="flex flex-wrap items-baseline gap-1.5">
            <span
              className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider"
              title={MTM_PNL_TOOLTIP}
            >
              Unrealized P&amp;L · MTM
            </span>
            <span
              className="text-muted-foreground font-mono text-[10px]"
              title={showPnl ? kalshiPnlCoverageTitle : 'Kalshi unrealized P&L'}
            >
              K
            </span>
            <span
              className={cn('font-mono text-sm font-semibold', pnlClass)}
              title={showPnl ? kalshiPnlCoverageTitle : 'P&L hidden — toggle on the Positions page'}
            >
              {showPnl ? kalshiPnlText : '•••'}
            </span>
            <span
              className="text-muted-foreground font-mono text-[10px]"
              title={polyPnlCoverageTitle}
            >
              P
            </span>
            <span
              className={cn(
                'font-mono text-sm font-semibold',
                !showPnl || polyPnlValue === null
                  ? 'text-muted-foreground'
                  : polyPnlValue >= 0
                    ? 'text-positive'
                    : 'text-negative',
              )}
              title={
                showPnl
                  ? polyPnlCoverageTitle
                  : 'P&L hidden — toggle on the Positions page'
              }
            >
              {!showPnl
                ? '•••'
                : polyPnlValue != null
                  ? formatDollars(polyPnlValue)
                  : polyPnl == null
                    ? '—'
                    : `— (${polyPnl.pricedPositions}/${polyPnl.totalPositions})`}
            </span>
            <UsRailUnrealized rail={usSummary} showPnl={showPnl} insideButton descriptionId={`${usDescription}-pnl`} />
          </div>
          <PositionsRefreshWarning positionsSync={positionsSync} showPnl={showPnl} title={kalshiPnlCoverageTitle} />
          <div className="flex flex-wrap items-baseline gap-1.5">
            <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
              Balance
            </span>
            <span
              className="text-muted-foreground font-mono text-[10px]"
              title={kalshiReportedBalanceTitle(keyMode)}
            >
              K
            </span>
            {balance !== null && !balanceHidden ? (
              <span className="text-foreground font-mono text-sm font-semibold">
                ${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            ) : (
              <span className="text-muted-foreground font-mono text-[11px]"
                title={balanceHidden ? ORG_BALANCE_HIDDEN_TITLE : undefined}>
                {balanceHidden ? ORG_BALANCE_HIDDEN_LABEL : '—'}
              </span>
            )}
            <span
              className="text-muted-foreground font-mono text-[10px]"
              title={
                polyBalance.source === 'org'
                  ? 'Polymarket org account (funder USDC)'
                  : polyBalance.source === 'personal'
                    ? 'Polymarket personal account (funder USDC)'
                    : 'Polymarket balance — no org poly keys and no personal connection'
              }
            >
              P
            </span>
            {polyBalance.usd !== null ? (
              <span className="text-foreground font-mono text-sm font-semibold">
                ${polyBalance.usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            ) : (
              <span className="text-muted-foreground font-mono text-[11px]"
                title={polyBalance.hidden ? ORG_BALANCE_HIDDEN_TITLE : undefined}>
                {polyBalance.hidden ? ORG_BALANCE_HIDDEN_LABEL : '—'}
              </span>
            )}
            <UsBalanceSlot rail={usSummary} focusable={false} descriptionId={`${usDescription}-balance`} className="text-muted-foreground"
              valueClassName="text-foreground font-mono text-sm font-semibold" />
          </div>
          <div
            className="text-muted-foreground font-mono text-[11px]"
            title="Kalshi contracts / mark value — Polymarket positions live on the Positions page"
          >
            <span className="text-foreground/80">{fmtQty(contractCount)}</span> contract
            {Math.round(contractCount) === 1 ? '' : 's'}{' '}
            <span className="text-foreground/60">
              / {groupedDollars(contractsValueDollars)} (K)
            </span>
          </div>
          <div
            className="text-muted-foreground font-mono text-[11px]"
            title="Kalshi open orders — Polymarket orders show in the positions panel and on the Positions page"
          >
            <span className="text-foreground/80">{openOrders.length}</span> order
            {openOrders.length === 1 ? '' : 's'} (K)
          </div>
        </div>
      )}
      <Crosshair
        size={16}
        className={expanded ? 'text-primary' : 'text-muted-foreground'}
      />
    </button>
  );
}
